import * as React from 'react';

import {RelativeTimestamp} from 'shells/timestamp/relative-timestamp';

import type {EmailAutomationStatusStatus} from 'nutshell-graphql-types';
import type {
    LeadStagesEdge_emailAutomationStatuses,
    LeadStagesEdge_emailAutomationStatuses_emailSequence_emailsQueued,
} from 'nutshell-graphql-types';

import {hasUnsentEmail} from './has-unsent-email';

const TEMPLATE_FIELDNAMES = ['firstName', 'lastName', 'companyName'];
const CANCELLED_STATUSES: EmailAutomationStatusStatus[] = [
    'CANCELLED_MANUALLY',
    'CANCELLED_AUTOMATICALLY',
];

export type Description = {
    tooltip?: string;
    warningTooltip?: string;
    description: React.ReactNode;
};

export function getEmailAutomationStatusDescription(
    emailAutomationStatus: LeadStagesEdge_emailAutomationStatuses,
    currentUserId?: string | null
): Description[] {
    const {emailSequence} = emailAutomationStatus;
    if (!emailSequence) {
        // If there isn't an email sequence, first check to see
        // if it happened to be cancelled before any emails went out
        if (isStatusCancelled(emailAutomationStatus.status)) {
            return [
                {
                    description: `Cancelled before any emails were sent`,
                },
            ];
        }

        // If not cancelled, then show problems as to why there is no sequence
        // @ts-expect-error
        return getEmailProblemDescription(emailAutomationStatus, currentUserId);
    }

    // Has the sequence completely finished sending?
    const completedSequenceMessageCount = getCompletedSequenceMessageCount(emailSequence);
    if (completedSequenceMessageCount) {
        if (isStatusCancelled(emailAutomationStatus.status)) {
            return [
                {
                    description: `Cancelled after ${completedSequenceMessageCount} ${
                        completedSequenceMessageCount > 1 ? 'emails' : 'email'
                    } sent`,
                },
            ];
        }

        if (completedSequenceMessageCount === 1) return [{description: 'Sent'}];

        return [{description: `Sent ${completedSequenceMessageCount} emails`}];
    }

    if (emailAutomationStatus.isPaused && emailAutomationStatus.sender) {
        const isSendingUserViewing = currentUserId === emailAutomationStatus.sender.id;

        return [
            {
                warningTooltip:
                    'When sending is turned back on, this email will be automatically rescheduled',
                description: `Will not send –  ${
                    isSendingUserViewing ? 'You’ve' : `${emailAutomationStatus.sender.name} has`
                } paused all automated messages`,
            },
        ];
    }

    // Emails are still queued to go out
    const nextEmail = getNextScheduledEmail(emailSequence);
    if (nextEmail) {
        return [
            {
                description: (
                    <div className='flex'>
                        <span>{`Sending "${nextEmail.subject}"\u00a0`}</span>
                        <RelativeTimestamp
                            datetime={nextEmail.deliveryTime}
                            format='hourHybrid'
                            lowercase={true}
                        />
                    </div>
                ),
            },
        ];
    }

    if (isStatusCancelled(emailAutomationStatus.status)) {
        return [
            {
                description: `Cancelled before any emails were sent`,
            },
        ];
    }

    return [];
}

type ProblematicSubscription = LeadStagesEdge_emailAutomationStatuses & {
    emailSequence?: null;
};

function getEmailProblemDescription(
    emailAutomationStatus: ProblematicSubscription,
    currentUserId: string
): Description[] {
    const primaryContact =
        emailAutomationStatus.lead.contacts.primaryEdge &&
        emailAutomationStatus.lead.contacts.primaryEdge.node;
    const primaryContactName = (primaryContact && primaryContact.name) || 'the related person';

    const problems = [];

    // Missing Person
    if (!primaryContact) {
        problems.push({
            tooltip: 'The first person added to the lead will receive this email.',
            description: 'Add a related person',
        });
    }

    // A primary person was found
    else {
        // Look for a missing email address
        if (!primaryContact.emailAddresses.primary) {
            problems.push({
                tooltip: 'This email can’t be scheduled until an email address is added.',
                description: `Add an email address for ${primaryContactName}`,
            });
        }

        // Look for missing template fields
        const missingFields = TEMPLATE_FIELDNAMES.filter(
            (fieldName) =>
                // @ts-expect-error
                emailAutomationStatus[fieldName].required && !emailAutomationStatus[fieldName].value
        );

        if (missingFields.length) {
            const isMissingFirst = missingFields.includes('firstName');
            const isMissingLast = missingFields.includes('lastName');
            const isMissingCompany = missingFields.includes('companyName');

            if (isMissingFirst && isMissingLast) {
                problems.push({
                    tooltip: 'This email can’t be scheduled until first and last names are added.',
                    description: `Add first and last names for ${primaryContactName}`,
                });
            } else if (isMissingFirst) {
                problems.push({
                    tooltip: 'This email can’t be scheduled until a first name is added.',
                    description: `Add a first name for ${primaryContactName}`,
                });
            } else if (isMissingLast) {
                problems.push({
                    tooltip: 'This email can’t be scheduled until a last name is added.',
                    description: `Add a last name for ${primaryContactName}`,
                });
            }

            if (isMissingCompany) {
                problems.push({
                    tooltip:
                        'This email can’t be scheduled until a company is added to this person.',
                    description: `Add a company to ${primaryContactName}`,
                });
            }
        }

        // Is it ready to send?
        if (emailAutomationStatus.status === 'READY_TO_SEND') {
            return [
                {
                    warningTooltip:
                        'This email was not sent automatically.  Click "Start" to queue it up!',
                    description: `This email is ready to go!`,
                },
            ];
        }

        if (emailAutomationStatus.status === 'PREEXISTING_LEAD_EXEMPTED') {
            return [
                {
                    description: `This email was not sent automatically because the lead existed before setting up this automated email.${
                        emailAutomationStatus.canStart ? ' Click “Start” to send it yourself!' : ''
                    }`,
                },
            ];
        }

        if (emailAutomationStatus.status === 'PREEXISTING_SEQUENCE_EXEMPTED') {
            return [
                {
                    description: `${primaryContactName} is already receiving this email`,
                },
            ];
        }

        // These entries go at the bottom, because we don't want to tell the
        // user their email won't send because its not assigned, when the
        // recipient doesn't have an email. Let them fix the email problem
        // first, then tell them its unassigned.
        if (!emailAutomationStatus.lead.assignee) {
            problems.push({
                tooltip: `This email was not sent automatically.${
                    emailAutomationStatus.canStart ? ' Click “Start” to send it yourself!' : ''
                }`,
                description: 'This email has not sent because the lead is not assigned',
            });
        }

        if (
            emailAutomationStatus.lead.assignee &&
            emailAutomationStatus.lead.assignee.__typename === 'Team'
        ) {
            // If the email automation status lead is assigned to a team
            problems.push({
                tooltip: `This email was not sent automatically.
                    ${emailAutomationStatus.canStart ? ' Click “Start” to send it yourself!' : ''}`,
                description: 'This email has not sent because the lead is assigned to a team',
            });
        }

        if (
            emailAutomationStatus.sender &&
            !emailAutomationStatus.sender.sendableEmailAddresses.filter(
                (emailAddresses) => emailAddresses.isEnabledForSending
            ).length
        ) {
            const isSendingUserViewing = currentUserId === emailAutomationStatus.sender.id;

            problems.push({
                tooltip: `Once ${
                    isSendingUserViewing ? 'you’ve' : 'they’ve'
                } enabled email sending within Nutshell, this will be good to go!`,
                description: `${
                    isSendingUserViewing ? 'You need' : `${emailAutomationStatus.sender.name} needs`
                } to configure email sending with Nutshell before this email can be sent`,
            });
        }
    }

    return problems;
}

function isStatusCancelled(status: EmailAutomationStatusStatus) {
    return CANCELLED_STATUSES.includes(status);
}

// @ts-expect-error
function getCompletedSequenceMessageCount(emailSequence): number {
    if (hasUnsentEmail(emailSequence)) return 0;

    return emailSequence.emailsQueued.length;
}

function getNextScheduledEmail(
    // @ts-expect-error
    emailSequence
): LeadStagesEdge_emailAutomationStatuses_emailSequence_emailsQueued | null | undefined {
    return (
        emailSequence.emailsQueued &&
        // @ts-expect-error
        emailSequence.emailsQueued.filter((email) => email.isNextScheduledEmail)[0]
    );
}
